import { useEffect, useState } from "react";

export function useLocalStorageState<T>(
  key: string,
  initialValue?: T,
  raw?: boolean
): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const storedValue = localStorage.getItem(key);

      if (typeof storedValue !== "string") {
        if (initialValue !== undefined) {
          const serializedInitial = raw
            ? String(initialValue)
            : JSON.stringify(initialValue);
          localStorage.setItem(key, serializedInitial);
        }

        return initialValue as T;
      }

      if (raw) {
        return storedValue as unknown as T;
      }

      return JSON.parse(storedValue || "null") as T;
    } catch {
      // localStorage access or JSON serialization can throw
      return initialValue as T;
    }
  });

  useEffect(() => {
    try {
      if (state === undefined) {
        localStorage.removeItem(key);
        return;
      }

      const serializedState = raw ? String(state) : JSON.stringify(state);
      localStorage.setItem(key, serializedState);
    } catch {
      // localStorage access or JSON serialization can throw
    }
  }, [key, raw, state]);

  return [state, setState];
}

export default useLocalStorageState;
